import { useMemo } from "react";

import { useGetAnswersQuery, useGetQuestionsQuery } from "@/redux/service";
import { AnswerResponse, QuestionResponse } from "@/redux/survey/types";
import { PaginatedResponse } from "@/redux/types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { SurveyViewDataFormat } from "@/redux/survey-views/types";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId";

export interface UseSurveyViewSelectableAnswersProps {
  surveyId?: number;
  dataFormat: keyof typeof SurveyViewDataFormat;
}

export interface SurveyViewSelectableAnswers {
  isLoading: boolean;
  isError: boolean;
  includedQuestionIds: Set<number> | null;
  selectableAnswers: AnswerResponse[];
  answersWithoutBlocks: AnswerResponse[];
  answersWithBlocks: AnswerResponse[];
  answeredQuestionIds: Set<number>;
  totalBlocksPerSharingRight: Record<string, number>;
  totalBlocks: number;
}

const toArray = <T>(data: T[] | PaginatedResponse<T> | undefined): T[] => {
  if (!data) return [];
  return isPaginatedResponse(data) ? data.results : data;
};

/**
 * Single source of truth for the counters and the selection of the survey view
 * form. Both the "selected" and the "answered" side of every counter are derived
 * from this one list, so they can never be built on different rules.
 */
export const useSurveyViewSelectableAnswers = ({
  surveyId,
  dataFormat,
}: UseSurveyViewSelectableAnswersProps): SurveyViewSelectableAnswers => {
  const organizationId = useOrganizationId();

  const questionsResponse = useGetQuestionsQuery(
    {
      survey: surveyId,
      dataFormat: SurveyViewDataFormat[dataFormat],
      pagination: "off",
    },
    { skip: !surveyId },
  );
  const answersResponse = useGetAnswersQuery(
    {
      survey: surveyId,
      organization: organizationId,
      pagination: "off",
      mode: "base",
    },
    { skip: !surveyId || !organizationId },
  );

  const includedQuestionIds = useMemo(() => {
    if (!questionsResponse.isSuccess) return null;
    return new Set(
      toArray<QuestionResponse>(questionsResponse.data).map(
        (question) => question.id,
      ),
    );
  }, [questionsResponse.isSuccess, questionsResponse.data]);

  const selectableAnswers = useMemo(() => {
    if (!includedQuestionIds || !answersResponse.isSuccess) return [];
    return toArray<AnswerResponse>(answersResponse.data).filter((answer) =>
      includedQuestionIds.has(answer.question),
    );
  }, [includedQuestionIds, answersResponse.isSuccess, answersResponse.data]);

  return useMemo(() => {
    const answersWithBlocks: AnswerResponse[] = [];
    const answersWithoutBlocks: AnswerResponse[] = [];
    const answeredQuestionIds = new Set<number>();
    const totalBlocksPerSharingRight: Record<string, number> = {};
    let totalBlocks = 0;

    selectableAnswers.forEach((answer) => {
      answeredQuestionIds.add(answer.question);

      if (answer.attachedBlocks.length === 0) {
        answersWithoutBlocks.push(answer);
        return;
      }

      answersWithBlocks.push(answer);
      answer.attachedBlocks.forEach((block) => {
        totalBlocksPerSharingRight[block.sharingRights] =
          (totalBlocksPerSharingRight[block.sharingRights] ?? 0) + 1;
        totalBlocks += 1;
      });
    });

    return {
      isLoading: questionsResponse.isLoading || answersResponse.isLoading,
      isError: questionsResponse.isError || answersResponse.isError,
      includedQuestionIds,
      selectableAnswers,
      answersWithoutBlocks,
      answersWithBlocks,
      answeredQuestionIds,
      totalBlocksPerSharingRight,
      totalBlocks,
    };
  }, [
    selectableAnswers,
    includedQuestionIds,
    questionsResponse.isLoading,
    questionsResponse.isError,
    answersResponse.isLoading,
    answersResponse.isError,
  ]);
};
